import { useEffect, useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, Download, FileSpreadsheet } from 'lucide-react'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Chip } from '@/components/ui/chip'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SkeletonRows } from '@/components/ui/skeleton'
import { num } from '@/lib/format'
import { useDebouncedValue } from '@/lib/useDebouncedValue'
import { cn } from '@/lib/utils'

import {
  downloadReport,
  defaultRange,
  rangeError,
  reportError,
  useReportOptions,
  useReportPage,
  type ReportKey,
  type ReportOptions,
  type ReportParams,
} from './api'
import { MultiSelect } from './MultiSelect'

/** A short closed list shown as chips: several may be on (multi) or at most one (single). */
export interface ChipFilter {
  param: string
  label: string
  options: { value: string; label: string }[]
  single?: boolean
}

export interface ReportDefinition {
  key: ReportKey
  title: string
  description: string
  /**
   * Which date From–To applies to; the first is the default. Absent: one date
   * only. `future` marks a date that can lie ahead (a promise's), so To may
   * pass today.
   */
  dateFields?: { value: string; label: string; future?: boolean }[]
  /** The people filter: its query parameter and what it is called on this report. */
  people: { param: string; label: string }
  /** Report-specific chip filters. */
  chips: ChipFilter[]
  /** Attendance: filter by role, from the options' designations. */
  designations?: boolean
}

const PAGE_SIZE = 50
const NUMERIC = new Set(['INTEGER', 'AMOUNT', 'DISTANCE'])

/**
 * One MIS report: filters, then the grid, then the files — each from the same
 * applied filters, so what is on screen is what downloads. Nothing runs until
 * the range passes the same check the server makes.
 */
export function ReportPage({ def }: { def: ReportDefinition }) {
  const options = useReportOptions(def.key)
  if (options.isLoading) {
    return (
      <div className="p-6">
        <SkeletonRows rows={6} />
      </div>
    )
  }
  if (options.isError || !options.data) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>This report could not be opened. You may not have access to it.</AlertDescription>
        </Alert>
      </div>
    )
  }
  return <ReportBody def={def} options={options.data} />
}

function ReportBody({ def, options }: { def: ReportDefinition; options: ReportOptions }) {
  const opening = defaultRange(options.today, options.maxRangeDays, !!def.dateFields?.[0]?.future)
  const [from, setFrom] = useState(opening.from)
  const [to, setTo] = useState(opening.to)
  const [dateField, setDateField] = useState(def.dateFields?.[0]?.value)
  const [nodes, setNodes] = useState<string[]>([])
  const [people, setPeople] = useState<string[]>([])
  const [designations, setDesignations] = useState<string[]>([])
  const [chips, setChips] = useState<Record<string, string[]>>({})

  const [applied, setApplied] = useState<ReportParams | null>(null)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 350)
  const [sort, setSort] = useState<number | null>(null)
  const [desc, setDesc] = useState(false)
  const [page, setPage] = useState(0)
  const [busy, setBusy] = useState<'xlsx' | 'csv' | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)

  const futureAllowed = !!def.dateFields?.find((f) => f.value === dateField)?.future
  const invalid = rangeError(from, to, options.today, options.maxRangeDays, futureAllowed)

  const params: ReportParams = useMemo(() => {
    const p: ReportParams = { from, to }
    if (dateField) p.dateField = dateField
    if (nodes.length) p.node = nodes
    if (people.length) p[def.people.param] = people
    if (designations.length) p.designation = designations
    for (const c of def.chips) {
      const v = chips[c.param] ?? []
      if (v.length) p[c.param] = c.single ? v[0] : v
    }
    return p
  }, [from, to, dateField, nodes, people, designations, chips, def])

  // A new search or sort starts at the first page.
  useEffect(() => setPage(0), [debouncedSearch, sort, desc, applied])

  const grid = useReportPage(def.key, applied, {
    search: debouncedSearch,
    sort,
    desc,
    page,
    size: PAGE_SIZE,
  })
  const [gridError, setGridError] = useState<string | null>(null)
  useEffect(() => {
    if (grid.error) void reportError(grid.error).then(setGridError)
    else setGridError(null)
  }, [grid.error])

  function apply() {
    if (invalid) return
    setFileError(null)
    setApplied(params)
  }

  async function download(format: 'xlsx' | 'csv') {
    if (invalid) return
    setBusy(format)
    setFileError(null)
    try {
      await downloadReport(def.key, params, format)
    } catch (err) {
      setFileError(await reportError(err))
    } finally {
      setBusy(null)
    }
  }

  function toggleChip(c: ChipFilter, value: string) {
    setChips((prev) => {
      const now = prev[c.param] ?? []
      const next = now.includes(value) ? now.filter((v) => v !== value) : c.single ? [value] : [...now, value]
      return { ...prev, [c.param]: next }
    })
  }

  function sortBy(column: number) {
    if (sort === column) setDesc((d) => !d)
    else {
      setSort(column)
      setDesc(false)
    }
  }

  const data = grid.data
  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-xl font-semibold text-navy-900">{def.title}</h1>
        <p className="text-sm text-slate-600">{def.description}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>
            Up to {options.maxRangeDays} days. You see only the records your role covers, here and in the files.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <Label htmlFor="report-from">From</Label>
              <Input id="report-from" type="date" value={from} max={futureAllowed ? undefined : options.today} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="report-to">To</Label>
              <Input id="report-to" type="date" value={to} max={futureAllowed ? undefined : options.today} onChange={(e) => setTo(e.target.value)} />
            </div>
            {def.dateFields && (
              <div className="space-y-1.5">
                <Label htmlFor="report-date-field">Dates apply to</Label>
                <select
                  id="report-date-field"
                  value={dateField}
                  onChange={(e) => setDateField(e.target.value)}
                  className="block w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"
                >
                  {def.dateFields.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {options.nodes.length > 0 && (
              <div className="space-y-1.5">
                <Label htmlFor="report-nodes">Branch / Region</Label>
                <MultiSelect
                  id="report-nodes"
                  value={nodes}
                  onChange={setNodes}
                  options={options.nodes.map((n) => ({ value: n.id, label: n.name, hint: n.type.toLowerCase() }))}
                />
              </div>
            )}
            {options.people.length > 1 && (
              <div className="space-y-1.5">
                <Label htmlFor="report-people">{def.people.label}</Label>
                <MultiSelect
                  id="report-people"
                  value={people}
                  onChange={setPeople}
                  options={options.people.map((p) => ({ value: p.id, label: p.name, hint: p.empId }))}
                />
              </div>
            )}
            {def.designations && (
              <div className="space-y-1.5">
                <Label htmlFor="report-designations">Designation</Label>
                <MultiSelect
                  id="report-designations"
                  value={designations}
                  onChange={setDesignations}
                  options={options.designations.map((d) => ({ value: d.code, label: d.label }))}
                />
              </div>
            )}
          </div>

          {def.chips.map((c) => (
            <div key={c.param} className="space-y-1.5">
              <p className="text-xs font-medium text-navy-800">{c.label}</p>
              <div className="flex flex-wrap gap-1.5">
                {c.single && (
                  <Chip active={(chips[c.param] ?? []).length === 0} onClick={() => setChips((p) => ({ ...p, [c.param]: [] }))}>
                    All
                  </Chip>
                )}
                {c.options.map((o) => (
                  <Chip key={o.value} active={(chips[c.param] ?? []).includes(o.value)} onClick={() => toggleChip(c, o.value)}>
                    {o.label}
                  </Chip>
                ))}
              </div>
            </div>
          ))}

          {invalid && (
            <Alert variant="warning">
              <AlertDescription>{invalid}</AlertDescription>
            </Alert>
          )}
          {fileError && (
            <Alert variant="destructive">
              <AlertDescription>{fileError}</AlertDescription>
            </Alert>
          )}

          <div className="flex flex-wrap gap-2">
            <Button onClick={apply} disabled={!!invalid}>
              Show report
            </Button>
            <Button variant="outline" onClick={() => download('xlsx')} disabled={!!invalid || busy !== null}>
              <FileSpreadsheet className="mr-1.5 h-4 w-4" />
              {busy === 'xlsx' ? 'Preparing…' : 'Excel'}
            </Button>
            <Button variant="outline" onClick={() => download('csv')} disabled={!!invalid || busy !== null}>
              <Download className="mr-1.5 h-4 w-4" />
              {busy === 'csv' ? 'Preparing…' : 'CSV'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {applied && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
            <CardTitle>Results</CardTitle>
            <Input
              aria-label="Search the results"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs"
            />
          </CardHeader>
          <CardContent>
            {gridError && (
              <Alert variant="destructive">
                <AlertDescription>{gridError}</AlertDescription>
              </Alert>
            )}
            {!data && grid.isFetching && <SkeletonRows rows={8} />}
            {data && (
              <>
                <div className={cn('overflow-x-auto', grid.isFetching && 'opacity-60')}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-2xs uppercase tracking-wider text-slate-500">
                        {data.columns.map((c, i) => (
                          <th key={c.header} className={cn('whitespace-nowrap py-2 pr-3', NUMERIC.has(c.type) && 'text-right')}>
                            <button type="button" onClick={() => sortBy(i)} className="inline-flex items-center gap-1 uppercase">
                              {c.header}
                              {sort === i && (desc ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />)}
                            </button>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.rows.map((row, r) => (
                        <tr key={r} className="border-b border-slate-100">
                          {row.map((cell, i) => {
                            const type = data.columns[i]?.type ?? 'TEXT'
                            return (
                            <td
                              key={i}
                              className={cn(
                                'whitespace-nowrap py-1.5 pr-3',
                                NUMERIC.has(type) && 'text-right tabular-nums',
                                ['DATE', 'TIME'].includes(type) && 'tabular-nums',
                              )}
                            >
                              {type === 'LINK' && cell ? (
                                <a href={cell} target="_blank" rel="noreferrer" className="text-brand underline-offset-4 hover:underline">
                                  View photo
                                </a>
                              ) : (
                                cell || '—'
                              )}
                            </td>
                            )
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 flex items-center justify-between text-xs text-slate-600">
                  <span>
                    {data.total === 0
                      ? 'No records for these filters.'
                      : `Showing ${num(page * PAGE_SIZE + 1)}–${num(page * PAGE_SIZE + data.rows.length)} of ${num(data.total)}`}
                  </span>
                  <span className="flex gap-4">
                    <button
                      type="button"
                      disabled={page === 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      className="text-2xs font-semibold uppercase tracking-wider text-navy-700 disabled:text-slate-300"
                    >
                      ← Prev
                    </button>
                    <button
                      type="button"
                      disabled={(page + 1) * PAGE_SIZE >= data.total}
                      onClick={() => setPage((p) => p + 1)}
                      className="text-2xs font-semibold uppercase tracking-wider text-navy-700 disabled:text-slate-300"
                    >
                      Next →
                    </button>
                  </span>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
